// src/hooks/useCommunalAdmin.ts

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import communalAdminService, { 
  type CommunalAdminCreateRequest, 
  type CommunalAdminResponse,
  type Commune 
} from '../services/communalAdmin.service';

/**
 * Hook for creating communal admins
 */
export const useCreateCommunalAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CommunalAdminCreateRequest) => 
      communalAdminService.createCommunalAdmin(data),
    onSuccess: (response) => {
      message.success('Administrateur communal créé avec succès');
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      queryClient.invalidateQueries({ queryKey: ['communes'] });
      queryClient.invalidateQueries({ queryKey: ['communal-admins'] });
      
      return response;
    },
    onError: (error: any) => {
      console.error('Error creating communal admin:', error);
      
      // Handle specific error messages
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Erreur lors de la création de l\'administrateur communal';
      
      if (errorMessage.includes('Email address is already registered')) {
        message.error('Cette adresse email est déjà utilisée');
      } else if (errorMessage.includes('validation')) {
        message.error('Données de saisie invalides. Veuillez vérifier les champs.');
      } else {
        message.error(errorMessage);
      }
      
      throw error;
    },
  });
};

/**
 * Hook for fetching all communes
 */
export const useCommunes = (options?: { onlyAvailable?: boolean }) => {
  return useQuery({
    queryKey: ['communes', { onlyAvailable: options?.onlyAvailable }],
    queryFn: () => {
      if (options?.onlyAvailable) {
        return communalAdminService.getAvailableCommunes();
      }
      return communalAdminService.getCommunes();
    },
    select: (data) => ({
      communes: data.data.communes || [],
      pagination: data.data.pagination,
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for checking email availability
 */
export const useCheckEmailAvailability = () => {
  return useMutation({
    mutationFn: (email: string) => communalAdminService.checkEmailAvailability(email),
    onError: (error) => {
      console.error('Error checking email availability:', error);
    },
  });
};

/**
 * Hook for password generation and validation
 */
export const usePasswordUtils = () => {
  const generatePassword = (length?: number) => {
    return communalAdminService.generatePassword(length);
  };

  const validatePassword = (password: string) => {
    return communalAdminService.validatePassword(password);
  };

  const validatePhone = (phone: string) => {
    return communalAdminService.validatePhone(phone);
  };

  return {
    generatePassword,
    validatePassword,
    validatePhone,
  };
};

/**
 * Hook for managing working hours
 */
export const useWorkingHours = () => {
  const getDefaultWorkingHours = () => {
    return communalAdminService.getDefaultWorkingHours();
  };

  const formatWorkingHours = (workingHours: Record<string, any>) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = {
      monday: 'Lundi',
      tuesday: 'Mardi', 
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche',
    };

    return days.map(day => ({
      day,
      displayName: dayNames[day as keyof typeof dayNames],
      ...workingHours[day],
    }));
  };

  return {
    getDefaultWorkingHours,
    formatWorkingHours,
  };
};